import React from 'react';
import {
  RadarChart as RechartsRadarBase,
  PolarGrid as PolarGridBase,
  PolarAngleAxis as PolarAngleAxisBase,
  PolarRadiusAxis as PolarRadiusAxisBase,
  Radar as RadarBase,
  Legend as LegendBase,
  ResponsiveContainer,
  Tooltip as TooltipBase,
} from 'recharts';

// Recharts v3 + older react-scripts TS workaround
const RechartsRadar = RechartsRadarBase as any;
const PolarGrid = PolarGridBase as any;
const PolarAngleAxis = PolarAngleAxisBase as any;
const PolarRadiusAxis = PolarRadiusAxisBase as any;
const Radar = RadarBase as any;
const Legend = LegendBase as any;
const Tooltip = TooltipBase as any;

export interface RadarDataset {
  name: string;
  color: string;
  fillOpacity?: number;
  values: Record<string, number>;
}

export interface RadarAxis {
  key: string;
  label: string;
  description?: string;
}

interface RadarChartProps {
  axes: RadarAxis[];
  datasets: RadarDataset[];
  size?: 'sm' | 'card' | 'md' | 'lg';
  showLegend?: boolean;
  showTooltip?: boolean;
  maxValue?: number;
  className?: string;
}

const SIZE_MAP = { sm: 160, card: 200, md: 300, lg: 420 };

// Custom tick that renders an SVG <title> for native browser tooltip (shows axis.description on hover)
const makeCustomTick = (axes: RadarAxis[], fontSize: number) => {
  return function CustomTick({ x, y, payload, textAnchor }: any) {
    const axis = axes.find(a => a.label === payload.value);
    return (
      <g transform={`translate(${x},${y})`}>
        {axis?.description && <title>{axis.description}</title>}
        <text
          textAnchor={textAnchor || 'middle'}
          fill="#6b7280"
          fontSize={fontSize}
          style={{ cursor: axis?.description ? 'help' : 'default' }}
        >
          {payload.value}
        </text>
      </g>
    );
  };
};

export const RadarChartComponent: React.FC<RadarChartProps> = ({
  axes,
  datasets,
  size = 'md',
  showLegend = true,
  showTooltip = true,
  maxValue = 10,
  className = '',
}) => {
  const chartData = axes.map(axis => {
    const point: Record<string, any> = { axis: axis.label, fullKey: axis.key };
    datasets.forEach(ds => {
      point[ds.name] = ds.values[axis.key] ?? 0;
    });
    return point;
  });

  const dim = SIZE_MAP[size];
  const isSmall = size === 'sm' || size === 'card';
  const CustomTick = makeCustomTick(axes, isSmall ? 9 : 11);
  const outerRadius = isSmall ? '55%' : '70%';

  return (
    <div className={className}>
      <ResponsiveContainer width={isSmall ? dim : '100%'} height={dim}>
        <RechartsRadar cx="50%" cy="50%" outerRadius={outerRadius} data={chartData}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis
            dataKey="axis"
            tick={<CustomTick />}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, maxValue]}
            tick={false}
            axisLine={false}
          />
          {datasets.map(ds => (
            <Radar
              key={ds.name}
              name={ds.name}
              dataKey={ds.name}
              stroke={ds.color}
              fill={ds.color}
              fillOpacity={ds.fillOpacity ?? 0.15}
              strokeWidth={size === 'sm' ? 1.5 : 2}
            />
          ))}
          {showTooltip && <Tooltip formatter={(val: any) => [Number(val).toFixed(1), '']} />}
          {showLegend && size !== 'sm' && <Legend />}
        </RechartsRadar>
      </ResponsiveContainer>
    </div>
  );
};
